import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['tax-aware-fire-planning'];

export default function TaxAwareFirePlanningPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['budgeting-from-take-home-pay-not-gross-fantasy', 'what-if-i-retire-earlier']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'A plan that ignores taxes can be directionally interesting and still operationally misleading.',
          'Tax treatment, deductions, and account structure meaningfully affect how a household experiences both the accumulation years and retirement itself.',
          'Per Diem brings tax context closer to the planning model so households are not constantly stitching it in later.',
        ]}
      />

      <ArticleSection title="A surprising amount of planning still happens pre-tax in spirit">
        <ArticleParagraph>
          Many FIRE plans are more sophisticated than the average retirement calculator, but they
          still quietly flatten taxes into a vague assumption. That is understandable at first. Tax
          modeling is messy, and households usually want clarity more than perfect precision.
        </ArticleParagraph>
        <ArticleParagraph>
          The problem is that taxes are not a rounding error. They shape take-home pay now and
          spending power later.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileBudget}
        alt="Per Diem mobile budget screen"
        caption="Tax-aware planning matters because the spendable number a household can really use is almost never the headline number it first imagines."
        variant="phone"
      />

      <ArticleSection title="Why tax awareness changes the plan">
        <ArticleBullets
          items={[
            'It affects how a household interprets current income and budget flexibility.',
            'It changes how different account types contribute to future spending power.',
            'It matters when evaluating deductions, payroll choices, and the true impact of compensation structure.',
            'It gives a more grounded picture of what retirement cash flow may actually feel like.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="Account type matters more than a single total">
        <ArticleParagraph>
          Two households can show similar balances and still have very different planning pictures
          depending on where that money lives. Taxable accounts, retirement accounts, HSAs, and
          education vehicles do not behave the same way, and the planning model should acknowledge
          that.
        </ArticleParagraph>
        <ArticleParagraph>
          That is why Per Diem treats account-type context as part of the planning picture instead
          of collapsing everything into one undifferentiated number.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.balanceSheet}
        alt="Per Diem balance sheet"
        caption="A richer account picture makes long-term planning more believable because the household is not pretending all dollars behave the same way."
      />

      <ArticleSection title="The goal is grounded confidence, not fake precision">
        <ArticleParagraph>
          Tax-aware planning does not require pretending the future can be modeled down to the last
          dollar. It just means acknowledging the major forces that change what a plan really means
          in practice.
        </ArticleParagraph>
        <ArticleParagraph>
          That is the role taxes play in Per Diem. Not to overwhelm the household with complexity,
          but to remove one of the biggest sources of hidden optimism from the plan.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
